import React, { useEffect, useState, useMemo } from "react";
import { DatePicker, Select, Input, InputNumber, Button, Table, Tag, Space, message, Card, Spin, Tooltip, Tabs } from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useKeycloak } from "@react-keycloak/web";
import { getWorkStatusReqeustsInOrg, getWorkStatusRequestByUserId, insertWorkStatusRequest,
  updateWorkStatusRequest, deleteWorkStatusRequest,approveWorkStatusRequest,rejectWorkStatusRequest,
  calculateHolidayDays, clearCalculation
 } from "../../redux/slieces/workStatusAppSlice";
 import { clearWeekPlanState } from "../../redux/slieces/weekPlanSlice";
 import { clearMonthPlanState } from "../../redux/slieces/monthPlanSlice";
 import { clearSummaryEmployeeState } from "../../redux/slieces/summaryEmplyeeSlice";
 import notificationBus from "../../events/notificationBus";
 import { dayStatuses } from "../../core/utils/weekPlanUtils";
 import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  CalendarOutlined,
  FilterOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined,
  CommentOutlined,
  StopOutlined,
} from "@ant-design/icons";
import logger from "../../core/utils/logger";
import {useTranslation } from 'react-i18next'
import styles from "./WorkStatusApplication.module.css";
import WorkStatusCalendar from "./WorkStatusCalendar";

const { RangePicker } = DatePicker;
const { Option } = Select;

const statusMap = {
  0: "Pending",
  1: "Requested",
  2: "Approved",
  3: "Rejected",
  4: "Cancelled",
};
const statusMetaMap = {
    0: { label: "Pending", color: "default" },
    1: { label: "Requested", color: "blue" },
    2: { label: "Approved", color: "green" },
    3: { label: "Rejected", color: "red" },
    4: { label: "Cancelled", color: "orange" },
};
const reverseStatusMap = {
 "Pending": 0,
  "Requested": 1,
  "Approved": 2,
  "Rejected": 3,
  "Cancelled": 4,

};
const dayStatusMap = dayStatuses.reduce((acc, label, index) => {
  acc[label]=index;
  return acc;
}, {});  


const testData = [
    {
        key: 1,
        from: "2025-03-25",
        to: "2025-03-27",
        reason: "Vacation",
        status: "Applied",
      },
      {
        key: 2,
        from: "2025-03-20",
        to: "2025-03-22",
        reason: "Remote Work",
        status: "Approved",
      },
      {
        key: 3,
        from: "2025-03-10",
        to: "2025-03-11",
        reason: "Training",
        status: "Approved",
      },
      {
        key: 4,
        from: "2025-01-15",
        to: "2025-01-16",
        reason: "Sick",
        status: "Approved",
      },
      {
        key: 5,
        from: "2024-12-01",
        to: "2024-12-05",
        reason: "Vacation",
        status: "Approved",
      },
      {
        key: 6,
        from: "2024-11-10",
        to: "2024-11-12",
        reason: "Other",
        status: "Applied",
      }
    ];
  

const WorkStatusApplication = ({test=false}) => {
    const [dateRange, setDateRange] = useState([]);
    const [reason, setReason] = useState("");
    const [holidayDays, setHolidayDays] = useState(null);
    const [comment, setComment] = useState("");
    const [applications, setApplications] = useState([]);
    const [filterStatus, setFilterStatus] = useState(null);
    const [filterMonths, setFilterMonths] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [activeTab, setActiveTab] = useState("list");
    const { t } = useTranslation();
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768)
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [])
    const translatedStatusMap = useMemo(() => ({
        0: t("Pending"),
        1: t("Requested"),
        2: t("Approved"),
        3: t("Rejected"),
        4: t("Cancelled"),
    }), [t ]);
    const translatedReasonMap = useMemo(() => {
        const map= {};
        dayStatuses.forEach((label) => {
            map[label] = t(`reasons.${label.toLowerCase()}`) || label;
        });
        return map;
    }, [dayStatuses, t]);
    const dispatch = useDispatch();
    const { keycloak, initialized } = useKeycloak();
    const { loading, error, workStatusRequests, suggestedDays, suggestedWorkDays, calculationLoading } = useSelector((state) => state.workStatusApp || {});
    const adminOrgId=keycloak.tokenParsed && keycloak.tokenParsed.organisationId
    ? Array.isArray(keycloak.tokenParsed.organisationId)
      ? keycloak.tokenParsed.organisationId[0]
      : keycloak.tokenParsed.organisationId
    : null;
    const hasMediAdminRole = keycloak.hasRealmRole('mediadmin');
    const currentUserId = keycloak.tokenParsed?.sub;
    const userEmail = keycloak.tokenParsed?.email;
    useEffect(() => {
      logger.log("User ID:", currentUserId);
        if (!initialized) return;
        if (test) {
        setApplications(testData);
        } else if (hasMediAdminRole) {
        dispatch(getWorkStatusReqeustsInOrg());
        } else {
        dispatch(getWorkStatusRequestByUserId());
        }

        }, [dispatch,initialized,test]);
    useEffect(() => {
      if (test) {
        setApplications(testData);
        } else {
          const mapped=(workStatusRequests || []).map((req)=>(
            {
              key:req.id,
              name:req.userName,
              userName: req.userName,
              from: dayjs(req.dateFrom).format("YYYY-MM-DD"),
              to: dayjs(req.dateTo).format("YYYY-MM-DD"),
              reason:dayStatuses[req.dayStatus],
              reasonCode: req.dayStatus,
              status: statusMap[req.requestStatus],
              statusCode: req.requestStatus,
              email: req.email,
              holidayDays: req.holidayDays,
              comment: req.comment,
              original:req
            }
          ));
      

      setApplications(mapped);
      }
    }, [workStatusRequests]);

    const isHoliday = reason === "Holiday";
    // Reasons that auto-suggest a day count from the work schedule (Holiday + Time Compensation)
    const suggestsDays = reason === "Holiday" || reason === "TimeCompensation";

    // Auto-calculate suggested days when dateRange changes and the reason suggests days (debounced)
    useEffect(() => {
        if (suggestsDays && dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
            const timer = setTimeout(() => {
                const dateFrom = dayjs(dateRange[0]).format("YYYY-MM-DD");
                const dateTo = dayjs(dateRange[1]).format("YYYY-MM-DD");
                dispatch(calculateHolidayDays({ dateFrom, dateTo }));
            }, 300);
            return () => clearTimeout(timer);
        } else {
            dispatch(clearCalculation());
            setHolidayDays(null);
        }
    }, [dateRange, reason, dispatch]);

    // Pre-fill holidayDays when suggestion arrives
    useEffect(() => {
        if (suggestedDays !== null && suggestedDays !== undefined) {
            setHolidayDays(suggestedDays);
        }
    }, [suggestedDays]);

    const handleApply = () => {
      if(!dateRange.length || !reason){
          message.warning( t("PleaseSelectDateAndReason"));
        return;
      }

    const dto={
      id:0,
      userId:currentUserId,
      userName:keycloak.tokenParsed?.name,
      dateFrom:new Date(dateRange[0]).toISOString(),
      dateTo:new Date(dateRange[1]).toISOString(),
      dayStatus:dayStatusMap[reason],
      requestStatus:reverseStatusMap["Requested"],
      organizationId:adminOrgId,
      email:userEmail,
      holidayDays: suggestsDays ? holidayDays : null,
      comment: suggestsDays && comment ? comment : null
    }
    dispatch(insertWorkStatusRequest({workStatusRequest:dto})).unwrap().then(()=>{
      notificationBus.emit('notify',{
        type:'success',
        message:t("WorkStatusRequestAppliedSuccess")
      });
      setDateRange([]);
      setReason("");
      setHolidayDays(null);
      setComment("");
      dispatch(clearCalculation());
    }).catch((error)=>{
      notificationBus.emit('notify',{
        type:'error',
        message:error.message
      });
    });
  };
    // An approve/cancel/delete/reject may have auto-changed the employee's week plan
    // (backend applies approved absences to future weeks + activates them, and restores
    // on cancel/delete). Clear the cached week/month/summary state so those views refetch
    // fresh the next time they're opened instead of showing stale data.
    const invalidatePlanCaches = () => {
      dispatch(clearWeekPlanState());
      dispatch(clearMonthPlanState());
      dispatch(clearSummaryEmployeeState());
    };

    const deleteApplication = (key) => {
      logger.log("Delete application:", key);
      if(!hasMediAdminRole) return;
      const application=applications.find((app)=>app.key===key);
      if(!application){
        return;
      }
      dispatch(deleteWorkStatusRequest({workStatusRequestId:key})).unwrap().then(()=>{
        invalidatePlanCaches();
        notificationBus.emit('notify',{
          type:'success',
          message:t("WorkStatusRequestDeletedSuccess")
        });
      }).catch((error)=>{
        notificationBus.emit('notify',{
          type:'error',
          message:error.message
        });
      });
    };
    const approveApplication = (key) => {
      logger.log("Approve application:", key);
      if(!hasMediAdminRole) return;
      const application=applications.find((app)=>app.key===key);
      if(!application){
        return;
      }
      dispatch(approveWorkStatusRequest({workStatusRequestId:key})).unwrap().then(()=>{
        invalidatePlanCaches();
        notificationBus.emit('notify',{
          type:'success',
          message:t("WorkStatusRequestApprovedSuccess")
        });
      }).catch((error)=>{
        notificationBus.emit('notify',{
          type:'error',
          message:error.message
        });
      });
    };
    const rejectApplication = (key) => {
      logger.log("Reject application:", key);
      if(!hasMediAdminRole) return;
      const application=applications.find((app)=>app.key===key);
      if(!application){
        return;
      }
      dispatch(rejectWorkStatusRequest({workStatusRequestId:key})).unwrap().then(()=>{
        invalidatePlanCaches();
        notificationBus.emit('notify',{
          type:'success',
          message:t("WorkStatusRequestRejectedSuccess")
        });
      }).catch((error)=>{
        notificationBus.emit('notify',{
          type:'error',
          message:error.message
        });
      });
    };

    // A user may withdraw their OWN application while it is still pending
    // (Requested) or already Approved. The backend flips it to Cancelled
    // immediately and notifies the admins — no admin approval is required.
    const canCancel = (record) =>
      record.email === userEmail &&
      (statusMap[record.statusCode] === "Requested" || statusMap[record.statusCode] === "Approved");

    const cancelApplication = (key) => {
      logger.log("Cancel application:", key);
      const application = applications.find((app) => app.key === key);
      if (!application || !canCancel(application)) {
        return;
      }
      const dto = {
        ...application.original,
        requestStatus: reverseStatusMap["Cancelled"],
      };
      dispatch(updateWorkStatusRequest({ workStatusRequest: dto })).unwrap().then(() => {
        invalidatePlanCaches();
        notificationBus.emit('notify', {
          type: 'success',
          message: t("WorkStatusRequestCancelledSuccess")
        });
      }).catch((error) => {
        notificationBus.emit('notify', {
          type: 'error',
          message: error.message
        });
      });
    };

    const filteredApplications=useMemo(()=>{
        return applications.filter(app=>{
            const appDate=dayjs(app.from);
            const now=dayjs();
            
            if(filterStatus && app.status!==filterStatus){
                return false;
            }
            if(filterMonths && appDate.isBefore(now.subtract(filterMonths, "month"))){
                return false;
            }
            return true;
        
        });

    },[applications, filterStatus, filterMonths]);

      const columns = [
        {
          title: t("name"),
          dataIndex: "name",
          key: "name",
          responsive: ["xs", "sm", "md", "lg"],
        },
        {
          title: t("from"),
          dataIndex: "from",
          key: "from",
          responsive: ["xs", "sm", "md", "lg"],
        },
        {
          title: t("to"),
          dataIndex: "to",
          key: "to",
          responsive: ["xs", "sm", "md", "lg"],
        },
        {
          title: t("reason"),
          dataIndex: "reason",
            key: "reason",
           render: (_, record) => {
               const translatedReason = t(`reasons.${record.reason?.toLowerCase()}`) || record.reason;
                return <Tag>{translatedReason}</Tag>;
            },
          responsive: ["sm", "md", "lg"],
        },
        {
          title: t("holidayDaysColumn"),
          dataIndex: "holidayDays",
          key: "holidayDays",
          render: (_, record) => {
            if (record.reasonCode !== 1 && record.reasonCode !== 5) return null;
            return record.holidayDays != null ? record.holidayDays : "—";
          },
          responsive: ["md", "lg"],
        },
        {
          title: t("commentColumn"),
          key: "comment",
          render: (_, record) => {
            if (!record.comment) return null;
            const truncated = record.comment.length > 50
              ? record.comment.substring(0, 50) + "..."
              : record.comment;
            return (
              <Tooltip title={record.comment}>
                <span><CommentOutlined style={{ marginRight: 4 }} />{truncated}</span>
              </Tooltip>
            );
          },
          responsive: ["md", "lg"],
        },
        {
          title: t("status"),
          dataIndex: "status",
          key: "status",
            render: (_, record) => {
                const statusCode = record.statusCode;
                const color = statusMetaMap[statusCode]?.color || "default";
                const label = t(record.status);
                return <Tag color={color}>{label}</Tag>;
            },
          responsive: ["xs", "sm", "md", "lg"],
        },
        {
          title: t("action"),
          key: "action",
          render: (_, record) => {
          
            return (
              <Space>
            
                {
               
                (statusMap[record.statusCode] === "Requested" && hasMediAdminRole) && (
                  <Button
                    type="link"
                    icon={<CheckCircleOutlined />}
                    onClick={() => approveApplication(record.key)}
                    title={t("approve")}
                    data-testid={`approve-btn-${record.key}`}
                  />
                )}
                {(statusMap[record.statusCode] === "Requested" && hasMediAdminRole) && (
                  <Button
                    type="link"
                    icon={<CloseCircleOutlined />}
                    danger
                    onClick={() => rejectApplication(record.key)}
                    title={t("Reject")}
                    data-testid={`reject-btn-${record.key}`}
                  />
                )}
                {canCancel(record) && (
                  <Button
                    type="link"
                    icon={<StopOutlined />}
                    onClick={() => cancelApplication(record.key)}
                    title={t("cancelApplication")}
                    data-testid={`cancel-btn-${record.key}`}
                  />
                )}
                {hasMediAdminRole && (
                  <Button
                    type="link"
                    icon={<DeleteOutlined />}
                    onClick={() => deleteApplication(record.key)}
                    title={t("delete")}
                    data-testid={`delete-btn-${record.key}`}
                  />
                )}
              </Space>
            );
          },
          responsive: ["sm", "md", "lg"], 
        }
      ];

      return (
    <div className={styles.workStatusContainer} data-testid="work-status-container">
      {/* Header Section */}
      <div className={styles.headerSection}>
        <div className={styles.titleContainer}>
          <CalendarOutlined className={styles.titleIcon} />
          <h1 className={styles.pageTitle} data-testid="work-status-title">{t("WorkStatusApplication")}</h1>
        </div>
      </div>

      {/* Application Form Card */}
      <Card className={styles.formCard} title={
        <div className={styles.cardTitle}>
          <PlusOutlined className={styles.cardIcon} />
          <span>{t("newApplication")}</span>
        </div>
      }>
        <div className={styles.formContent}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                <CalendarOutlined className={styles.labelIcon} />
                {t("dateRange")}
              </label>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates || [])}
                className={styles.dateRangePicker}
                size="large"
                allowClear
                placeholder={[t("startDate"), t("endDate")]}
                data-testid="date-range-picker"
              />
            </div>
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                {t("reason")}
              </label>
              <Select
                placeholder={t("SelectReason")}
                value={reason}
                onChange={(value) => setReason(value)}
                className={styles.reasonSelect}
                size="large"
                data-testid="reason-select"
              >
                {dayStatuses
                  .filter((status) => status === "Holiday" || status === "Sick" || status === "ChildSick" || status === "TimeCompensation")
                  .map((status) => (
                    <Option key={status} value={status}>
                      {t(`statuses.${status?.toLowerCase()}`)}
                    </Option>
                  ))}
              </Select>
            </div>
          </div>

          {/* Day-count + comment fields (Holiday and Time Compensation) */}
          {suggestsDays && (
            <div className={`${styles.formRow} ${styles.holidayFieldsRow}`} data-testid="holiday-fields">
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  <CalendarOutlined className={styles.labelIcon} />
                  {isHoliday ? t("holidayDays") : t("numberOfDays")}
                </label>
                {calculationLoading ? (
                  <Spin size="small" />
                ) : (
                  <div className={styles.holidayDaysInputWrapper}>
                    <InputNumber
                      value={holidayDays}
                      onChange={(value) => setHolidayDays(value)}
                      min={0}
                      step={0.5}
                      precision={1}
                      size="large"
                      placeholder={t("holidayDaysPlaceholder")}
                      data-testid="holiday-days-input"
                    />
                    {suggestedDays !== null && suggestedWorkDays && suggestedWorkDays.length > 0 && (
                      <Tooltip title={`${t("basedOnSchedule")}: ${suggestedWorkDays.map(d => t(`days.${d.toLowerCase()}`)).join(", ")}`}>
                        <InfoCircleOutlined className={styles.infoIcon} />
                      </Tooltip>
                    )}
                  </div>
                )}
                {suggestedDays !== null && holidayDays !== suggestedDays && (
                  <small className={styles.overrideHint} data-testid="suggested-days-hint">
                    {t("suggestedDaysInfo", { days: suggestedDays })}
                  </small>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  {t("holidayComment")}
                </label>
                <Input.TextArea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  maxLength={500}
                  showCount
                  rows={2}
                  placeholder={t("holidayCommentPlaceholder")}
                  size="large"
                  data-testid="comment-textarea"
                />
              </div>
            </div>
          )}

          <Button
            type="primary"
            onClick={handleApply}
            className={styles.submitButton}
            size="large"
            icon={<PlusOutlined />}
            loading={loading}
            data-testid="submit-application"
          >
            {t("Apply")}
          </Button>
        </div>
      </Card>

      {/* Tabs for List and Calendar Views */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className={styles.viewTabs}
        items={[
          {
            key: "list",
            label: (
              <span>
                <UnorderedListOutlined />
                {t("applicationsList")}
              </span>
            ),
            children: (
              <>
                {/* Filters Section */}
                <Card className={styles.filtersCard} title={
                  <div className={styles.cardTitle}>
                    <FilterOutlined className={styles.cardIcon} />
                    <span>{t("filters")}</span>
                  </div>
                }>
                  <div className={styles.filtersContent}>
                    <div className={styles.filterGroup}>
                      <label className={styles.filterLabel}>{t("status")}</label>
                      <Select
                        placeholder={t("FilterByStatus")}
                        allowClear
                        className={styles.filterSelect}
                        onChange={setFilterStatus}
                        size="large"
                      >
                        {Object.keys(translatedStatusMap).map((status) => (
                          <Option key={status} value={statusMap[status]}>
                            {translatedStatusMap[status]}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div className={styles.filterGroup}>
                      <label className={styles.filterLabel}>{t("timeRange")}</label>
                      <Select
                        placeholder={t("lastmonths")}
                        allowClear
                        className={styles.filterSelect}
                        onChange={setFilterMonths}
                        size="large"
                      >
                        <Option value={3}>{t("last3months")}</Option>
                        <Option value={6}>{t("last6months")}</Option>
                        <Option value={9}>{t("last9months")}</Option>
                      </Select>
                    </div>
                  </div>
                </Card>
                {/* Applications List */}
                <Card className={styles.applicationsCard} title={
                  <div className={styles.cardTitle}>
                    <span>{t("myApplications")} ({filteredApplications.length})</span>
                  </div>
                }>
                  {loading ? (
                    <div className={styles.loadingContainer}>
                      <Spin size="large" />
                      <p className={styles.loadingText}>{t("loading")}</p>
                    </div>
                  ) : filteredApplications.length === 0 ? (
                    <div className={styles.emptyState}>
                      <CalendarOutlined className={styles.emptyIcon} />
                      <h3 className={styles.emptyTitle}>{t("noApplications")}</h3>
                      <p className={styles.emptyDescription}>{t("noApplicationsDescription")}</p>
                    </div>
                  ) : isMobile ? (
                    <div className={styles.mobileCards}>
                      {filteredApplications.map((app) => (
                        <Card key={app.key} className={styles.mobileCard} size="small">
                          <div className={styles.mobileCardHeader}>
                            <div className={styles.mobileCardTitle}>
                              <CalendarOutlined className={styles.mobileCardIcon} />
                              <span className={styles.mobileCardName}>{app.name || t("anonymous")}</span>
                            </div>
                            <Tag
                              color={app.status === "Approved" ? "success" : app.status === "Rejected" ? "error" : app.status === "Requested" ? "processing" : "default"}
                              className={styles.statusTag}
                            >
                              {t(app.status)}
                            </Tag>
                          </div>
                          <div className={styles.mobileCardContent}>
                            <div className={styles.mobileCardRow}>
                              <span className={styles.mobileCardLabel}>{t("dateRange")}:</span>
                              <span className={styles.mobileCardValue}>{app.from} - {app.to}</span>
                            </div>
                            <div className={styles.mobileCardRow}>
                              <span className={styles.mobileCardLabel}>{t("reason")}:</span>
                              <span className={styles.mobileCardValue}>{t(`reasons.${app.reason?.toLowerCase()}`) || app.reason}</span>
                            </div>
                            {(app.reasonCode === 1 || app.reasonCode === 5) && app.holidayDays != null && (
                              <div className={styles.mobileCardRow}>
                                <span className={styles.mobileCardLabel}>{t("holidayDaysColumn")}:</span>
                                <span className={styles.mobileCardValue}>{app.holidayDays}</span>
                              </div>
                            )}
                            {app.comment && (
                              <div className={styles.mobileCardRow}>
                                <span className={styles.mobileCardLabel}>{t("commentColumn")}:</span>
                                <span className={styles.mobileCardValue}>{app.comment}</span>
                              </div>
                            )}
                          </div>
                          <div className={styles.mobileCardActions}>
                            {(app.status === "Requested" && hasMediAdminRole) && (
                              <>
                                <Tooltip title={t("approve")}>
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={<CheckCircleOutlined />}
                                    onClick={() => approveApplication(app.key)}
                                    className={styles.approveButton}
                                  >
                                    {t("approve")}
                                  </Button>
                                </Tooltip>
                                <Tooltip title={t("reject")}>
                                  <Button
                                    danger
                                    size="small"
                                    icon={<CloseCircleOutlined />}
                                    onClick={() => rejectApplication(app.key)}
                                    className={styles.rejectButton}
                                  >
                                    {t("reject")}
                                  </Button>
                                </Tooltip>
                              </>
                            )}
                            {canCancel(app) && (
                              <Tooltip title={t("cancelApplication")}>
                                <Button
                                  size="small"
                                  icon={<StopOutlined />}
                                  onClick={() => cancelApplication(app.key)}
                                  className={styles.cancelButton}
                                >
                                  {t("cancelApplication")}
                                </Button>
                              </Tooltip>
                            )}
                            {hasMediAdminRole && (
                              <Tooltip title={t("delete")}>
                                <Button
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => deleteApplication(app.key)}
                                  className={styles.deleteButton}
                                >
                                  {t("delete")}
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <Table
                      columns={columns}
                      dataSource={filteredApplications}
                      className={styles.applicationsTable}
                      data-testid="applications-table"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} ${t("of")} ${total} ${t("items")}`,
                      }}
                      scroll={{ x: true }}
                      loading={loading}
                    />
                  )}
                </Card>
              </>
            ),
          },
          {
            key: "calendar",
            label: (
              <span>
                <CalendarOutlined />
                {t("calendarView")}
              </span>
            ),
            children: (
              <WorkStatusCalendar
                applications={applications}
                isAdmin={hasMediAdminRole}
                onApprove={approveApplication}
                onReject={rejectApplication}
                loading={loading}
              />
            ),
          },
        ]}
      />
    </div>
      );




};

export default WorkStatusApplication;